import React, { useState, useEffect, useRef } from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(ArcElement, ChartTooltip, Legend, ChartDataLabels);

/**
 * DonutChart with inline insight overlays.
 *
 * Replaces the previous DonutChart + standalone InsightsList combo.
 * Insights now live in two visually integrated places:
 *
 *   1. Slice-bound WARN/BAD insights → small colored "!" badge on the
 *      relevant cost legend row (e.g. fees-related warning gets a dot next
 *      to "Etsy fees"). Hover or click reveals full text + suggestion +
 *      action button.
 *
 *   2. General insights AND all "good"-tone opportunities → a horizontal
 *      icon-only chip strip ("Notes & tips") below the legend. Hover or
 *      click reveals full text. Good-tone insights live here so their
 *      tappability is consistent with the other note chips.
 *
 * Tone → palette mapping is the same as the old InsightsList (good/warn/bad).
 */

const TONE_STYLES = {
  good: { bg: '#d1fae5', border: '#047857', text: '#064e3b' },
  warn: { bg: '#fef3c7', border: '#b45309', text: '#7c2d12' },
  bad:  { bg: '#fee2e2', border: '#9f1239', text: '#7f1d1d' },
};

/**
 * Heuristic — figure out which donut slice (if any) an insight refers to,
 * based on its icon and message text. Returns the slice key, or null
 * for "general" insights that go in the chip strip.
 */
function classifyInsight(ins) {
  const text = (ins.msg || '').toLowerCase();
  const icon = ins.icon || '';

  // Strong signals first — icons we deliberately use for specific slices
  if (icon === '🏷️' || /etsy is taking|effective fee/i.test(ins.msg)) return 'fees';
  if (icon === '📡' || /offsite ads/i.test(ins.msg)) return 'fees';
  if (icon === '📦' && /shipping/i.test(ins.msg)) return 'shipping';
  if (icon === '⏱️') return 'labor';
  if (icon === '🚨') return 'profit';
  if (icon === '✨') return 'profit';

  // Text fallbacks
  if (/profit|margin|losing/i.test(text)) return 'profit';
  if (/fee|etsy is taking/i.test(text)) return 'fees';
  if (/labor|hourly|hour/i.test(text)) return 'labor';
  if (/material|production cost/i.test(text)) return 'materials';
  if (/packaging/i.test(text)) return 'packaging';
  if (/shipping/i.test(text)) return 'shipping';

  return null; // general — goes in the chip strip below
}

/** Toned indicator badge — small colored circle with a glyph. */
function InsightBadge({ insight, onHover, onLeave, onClick, active }) {
  const tone = TONE_STYLES[insight.tone] || TONE_STYLES.warn;
  const glyph = insight.tone === 'good' ? '✓' : '!';
  return (
    <button
      type="button"
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onFocus={onHover}
      onBlur={onLeave}
      onClick={onClick}
      aria-label={`Insight: ${insight.msg}`}
      style={{
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        background: tone.bg,
        border: `1.5px solid ${tone.border}`,
        color: tone.text,
        fontFamily: 'Inter, sans-serif',
        fontSize: '0.78rem',
        fontWeight: 800,
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        cursor: 'pointer',
        transition: 'transform 0.12s, box-shadow 0.12s',
        transform: active ? 'scale(1.15)' : 'scale(1)',
        boxShadow: active ? `0 0 0 3px ${tone.bg}` : 'none',
        flexShrink: 0,
      }}
    >
      {glyph}
    </button>
  );
}

/** General-insight icon chip — emoji-only, larger, fully clickable. */
function InsightChip({ insight, onHover, onLeave, onClick, active }) {
  const tone = TONE_STYLES[insight.tone] || TONE_STYLES.warn;
  return (
    <button
      type="button"
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onFocus={onHover}
      onBlur={onLeave}
      onClick={onClick}
      aria-label={`Insight: ${insight.msg}`}
      title={insight.msg}
      style={{
        width: '36px',
        height: '36px',
        borderRadius: '0.55rem',
        background: tone.bg,
        border: `1.5px solid ${tone.border}`,
        fontSize: '1.1rem',
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        cursor: 'pointer',
        transition: 'transform 0.12s, box-shadow 0.12s',
        transform: active ? 'scale(1.1)' : 'scale(1)',
        boxShadow: active ? `0 0 0 3px ${tone.bg}` : '1px 1px 0 rgba(26,26,46,0.1)',
        flexShrink: 0,
      }}
    >
      <span aria-hidden="true">{insight.icon}</span>
    </button>
  );
}

/** Popover — appears beneath the legend with full text + action. */
function InsightPopover({ insight, onApplyAction, onClose }) {
  if (!insight) return null;
  const tone = TONE_STYLES[insight.tone] || TONE_STYLES.warn;
  return (
    <div
      style={{
        marginTop: '0.65rem',
        padding: '0.7rem 0.85rem',
        background: tone.bg,
        border: `1.5px solid ${tone.border}`,
        borderRadius: '0.5rem',
        color: tone.text,
        position: 'relative',
        boxShadow: '0 2px 8px rgba(26,26,46,0.08)',
      }}
      role="status"
    >
      <button
        onClick={onClose}
        aria-label="Close"
        style={{
          position: 'absolute',
          top: '0.3rem',
          right: '0.4rem',
          background: 'transparent',
          border: 'none',
          color: tone.text,
          opacity: 0.55,
          cursor: 'pointer',
          fontSize: '1rem',
          lineHeight: 1,
          padding: '0.15rem 0.3rem',
        }}
      >
        ×
      </button>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', paddingRight: '1.1rem' }}>
        <span style={{ fontSize: '1.1rem', flexShrink: 0, lineHeight: 1.2 }} aria-hidden="true">
          {insight.icon}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.86rem', fontWeight: 600, lineHeight: 1.4, margin: 0 }}>
            {insight.msg}
          </p>
          {insight.suggestion && (
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', lineHeight: 1.5, marginTop: '0.3rem', marginBottom: 0, opacity: 0.88 }}>
              {insight.suggestion}
            </p>
          )}
          {insight.action && onApplyAction && (
            <button
              onClick={() => { onApplyAction(insight.action); onClose && onClose(); }}
              style={{
                marginTop: '0.5rem',
                padding: '0.28rem 0.65rem',
                background: '#fefdf8',
                border: `1.5px solid ${tone.border}`,
                borderRadius: '0.35rem',
                color: tone.border,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '0.78rem',
                cursor: 'pointer',
                letterSpacing: '0.01em',
              }}
            >
              {insight.action.label} →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DonutChart({ result, insights = [], onApplyAction, flashMessage, onDismissFlash }) {
  // The active insight whose popover is currently shown.
  // Identified by a string key like "slice:fees:0" or "general:2".
  const [activeKey, setActiveKey] = useState(null);
  const cardRef = useRef(null);

  // Auto-dismiss flash after a beat
  useEffect(() => {
    if (!flashMessage || !onDismissFlash) return;
    const t = setTimeout(onDismissFlash, 4500);
    return () => clearTimeout(t);
  }, [flashMessage, onDismissFlash]);

  // Click-outside dismisses the popover
  useEffect(() => {
    if (!activeKey) return;
    const onDocClick = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        setActiveKey(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [activeKey]);

  if (!result || result.revenue <= 0) return null;
  const sym = result.currencySymbol || '$';

  const slices = [
    { key: 'materials', label: 'Materials',     value: result.productionCost,   color: '#fb923c' },
    { key: 'shipping',  label: 'Shipping cost', value: result.shippingCostPaid, color: '#a78bfa' },
    { key: 'packaging', label: 'Packaging',     value: result.packagingCost,    color: '#fbbf24' },
    { key: 'labor',     label: 'Labor',         value: result.laborCost,        color: '#f472b6' },
    { key: 'fees',      label: 'Etsy fees',     value: result.totalEtsyFees,    color: '#60a5fa' },
    { key: 'profit',
      label: result.profit >= 0 ? 'Your profit' : 'Your loss',
      value: Math.abs(result.profit),
      color: result.profit >= 0 ? '#34d399' : '#f87171' },
  ].filter(s => s.value > 0.005);

  const total = slices.reduce((a, s) => a + s.value, 0);

  // Bucket insights into slice-bound vs general.
  //
  // Only warn/bad insights stick to a slice as a small "!" badge — those
  // shout for attention via color + glyph and read as actionable.
  // "Good" insights are opportunities (e.g. "roll shipping into price",
  // "try bulk math") — the small ✓ badge looked like a passive checkmark
  // rather than something tappable, so we surface them as Notes chips
  // alongside the other clickable hints.
  const sliceInsights = {};
  const generalInsights = [];
  for (const ins of insights) {
    const sliceKey = classifyInsight(ins);
    const stickToSlice = sliceKey
      && ins.tone !== 'good'
      && slices.some(s => s.key === sliceKey);
    if (stickToSlice) {
      if (!sliceInsights[sliceKey]) sliceInsights[sliceKey] = [];
      sliceInsights[sliceKey].push(ins);
    } else {
      generalInsights.push(ins);
    }
  }

  const data = {
    labels: slices.map(s => s.label),
    datasets: [{
      data: slices.map(s => s.value),
      backgroundColor: slices.map(s => s.color),
      borderColor: '#1a1a2e',
      borderWidth: 2,
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: 18 },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1a1a2e',
        titleFont: { family: 'Inter, sans-serif', weight: '600', size: 13 },
        bodyFont: { family: 'Inter, sans-serif', size: 12 },
        padding: 10,
        cornerRadius: 6,
        callbacks: {
          label: (ctx) => {
            const pct = total > 0 ? ((ctx.raw / total) * 100).toFixed(1) : '0';
            return `  ${sym}${ctx.raw.toFixed(2)}  ·  ${pct}%`;
          },
        },
      },
      datalabels: {
        color: '#1a1a2e',
        font: { family: 'Inter, sans-serif', weight: 700, size: 11 },
        formatter: (value) => {
          const pct = total > 0 ? (value / total) * 100 : 0;
          return pct >= 4 ? `${pct.toFixed(0)}%` : '';
        },
      },
    },
    cutout: '58%',
  };

  // Resolve active insight from the active key
  const activeInsight = (() => {
    if (!activeKey) return null;
    if (activeKey.startsWith('general:')) {
      const idx = parseInt(activeKey.split(':')[1], 10);
      return generalInsights[idx] || null;
    }
    if (activeKey.startsWith('slice:')) {
      const [, sliceKey, idxStr] = activeKey.split(':');
      const idx = parseInt(idxStr, 10);
      return sliceInsights[sliceKey]?.[idx] || null;
    }
    return null;
  })();

  const totalInsights = insights.length;

  return (
    <div ref={cardRef} className="sketch-card h-full flex flex-col" style={{ position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
        <div>
          <h2 className="font-display text-2xl text-ink">Revenue Breakdown</h2>
          <p className="font-body text-sm text-ink/78 mb-3">
            For every {sym}1 a buyer pays you, here's the split.
          </p>
        </div>
        {totalInsights > 0 && (
          <span style={{
            background: '#fef3c7',
            border: '1.5px solid #b45309',
            color: '#7c2d12',
            padding: '0.2rem 0.55rem',
            borderRadius: '999px',
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.72rem',
            fontWeight: 700,
            letterSpacing: '0.02em',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem',
            flexShrink: 0,
            marginTop: '0.2rem',
          }} title={`${totalInsights} insight${totalInsights === 1 ? '' : 's'} — hover any marker to read`}>
            💡 {totalInsights}
          </span>
        )}
      </div>

      {/* Action-applied flash banner */}
      {flashMessage && (
        <div
          role="status"
          aria-live="polite"
          style={{
            padding: '0.55rem 0.75rem',
            background: '#d1fae5',
            border: '1.5px solid #047857',
            borderRadius: '0.5rem',
            marginBottom: '0.75rem',
            color: '#064e3b',
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.85rem',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '0.6rem',
          }}
        >
          <span style={{ flex: 1, lineHeight: 1.4 }}>
            {typeof flashMessage === 'string' ? flashMessage : flashMessage.message}
          </span>
          <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
            {typeof flashMessage === 'object' && flashMessage.undo && (
              <button
                onClick={flashMessage.undo}
                style={{
                  padding: '0.15rem 0.55rem',
                  background: '#ffffff',
                  border: '1.5px solid #047857',
                  borderRadius: '0.35rem',
                  color: '#047857',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                ↶ Undo
              </button>
            )}
            {onDismissFlash && (
              <button
                onClick={onDismissFlash}
                aria-label="Dismiss"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#047857',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  padding: '0 0.2rem',
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            )}
          </div>
        </div>
      )}

      {/* Donut */}
      <div className="relative" style={{ height: '240px' }}>
        <Doughnut data={data} options={options} />
      </div>

      {/* Legend with inline insight badges */}
      <div className="mt-4 flex-1 flex flex-col">
        <div className="space-y-1.5">
          {slices
            .slice()
            .sort((a, b) => b.value - a.value)
            .map(s => {
              const pct = (s.value / total) * 100;
              const isProfit = s.label.toLowerCase().includes('profit');
              const isLoss   = s.label.toLowerCase().includes('loss');
              const myInsights = sliceInsights[s.key] || [];
              return (
                <div
                  key={s.label}
                  className="flex items-center justify-between gap-2 py-1 px-2 rounded-md"
                  style={{ background: 'rgba(255,255,255,0.5)' }}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      style={{
                        display: 'inline-block',
                        width: '0.85rem', height: '0.85rem',
                        borderRadius: '0.25rem',
                        backgroundColor: s.color,
                        border: '1.5px solid #1a1a2e',
                        flexShrink: 0,
                      }}
                    />
                    <span className="font-body text-sm font-semibold text-ink truncate">
                      {s.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span
                      className="font-numbers text-sm"
                      style={{
                        fontWeight: 700,
                        color: isProfit ? '#047857' : isLoss ? '#9f1239' : '#1a1a2e',
                      }}
                    >
                      {sym}{s.value.toFixed(2)}
                    </span>
                    <span className="font-body text-xs text-ink/72 tabular-nums" style={{ minWidth: '3.2em', textAlign: 'right' }}>
                      {pct.toFixed(1)}%
                    </span>
                    {myInsights.map((ins, idx) => {
                      const k = `slice:${s.key}:${idx}`;
                      return (
                        <InsightBadge
                          key={k}
                          insight={ins}
                          active={activeKey === k}
                          onHover={() => setActiveKey(k)}
                          onLeave={() => { /* keep open if pinned via click */ }}
                          onClick={() => setActiveKey(activeKey === k ? null : k)}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
        </div>

        {/* General insights chip strip */}
        {generalInsights.length > 0 && (
          <div style={{
            marginTop: '0.85rem',
            paddingTop: '0.65rem',
            borderTop: '1px dashed rgba(26,26,46,0.15)',
          }}>
            <p style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'rgba(26,26,46,0.6)',
              marginBottom: '0.4rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              flexWrap: 'wrap',
            }}>
              <span>💡 Notes &amp; tips ({generalInsights.length})</span>
              <span style={{
                fontSize: '0.66rem',
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'none',
                color: 'rgba(26,26,46,0.5)',
              }}>
                — tap any to read
              </span>
            </p>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              {generalInsights.map((ins, idx) => {
                const k = `general:${idx}`;
                return (
                  <InsightChip
                    key={k}
                    insight={ins}
                    active={activeKey === k}
                    onHover={() => setActiveKey(k)}
                    onLeave={() => { /* keep open if pinned */ }}
                    onClick={() => setActiveKey(activeKey === k ? null : k)}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Popover for the active insight */}
        {activeInsight && (
          <InsightPopover
            insight={activeInsight}
            onApplyAction={onApplyAction}
            onClose={() => setActiveKey(null)}
          />
        )}
      </div>
    </div>
  );
}
